import { authenticatedFetch } from "./apiClient";

export interface PersonaGenerationRequest {
  page_url: string;
  domain_id: string;
  page_id: string;
}

export interface CustomPersonaGenerationRequest {
  page_url: string;
  domain_id: string;
  page_id: string;
  persona_description: string;
}

export interface DualPersonaGenerationResponse {
  success: boolean;
  message: string;
  persona_ids: string[];
}

export interface CustomPersonaGenerationResponse {
  success: boolean;
  message: string;
  persona_id: string;
}

export interface PersonaEnabledResponse {
  persona_id: string;
  is_enabled: boolean;
}

export class PersonaGenerationService {
  /**
   * Toggle whether a persona feeds generation.
   *
   * Backend-mediated: personas are `allow write: if false` in firestore.rules,
   * so the client cannot write `is_enabled` directly.
   */
  static async setPersonaEnabled(
    personaId: string,
    domainId: string,
    isEnabled: boolean,
  ): Promise<PersonaEnabledResponse> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/generations/personas/${personaId}/enabled`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain_id: domainId, is_enabled: isEnabled }),
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to update persona: ${response.statusText}`);
    }

    return response.json();
  }

  static async generateDualPersonas(
    request: PersonaGenerationRequest,
  ): Promise<DualPersonaGenerationResponse> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/generations/personas/dual`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw new Error(`Generation failed: ${response.statusText}`);
    }

    return response.json();
  }

  static async generateCustomPersona(
    request: CustomPersonaGenerationRequest,
  ): Promise<CustomPersonaGenerationResponse> {
    const response = await authenticatedFetch(
      `${import.meta.env.VITE_API_URL}/v1/generations/personas/custom`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw new Error(`Generation failed: ${response.statusText}`);
    }

    return response.json();
  }
}
